// "내 기기 정보" — 이 기기의 접속 ID·세션 비밀번호(+재생성)와, 지금 접속을 받을 수 없게 만드는 경고(서버 끊김·macOS 권한).
// 첫 화면 가운데 카드의 윗부분(2026-09-27 개편 — `RSP 화면.pdf`). 상태는 `useMyConnection` 이 준다.
import { IS_SHELL, SHELL } from '../../shell';
import type { MyConnection } from './useMyConnection';
import { useHostSession } from './useHostSession';

export function MyDeviceInfo({ conn }: { conn: MyConnection }) {
  const { deviceCode, sessionPw, regenerating, regenerate, offline, missing, openSettings, restartApp } = conn;
  // 원격지원을 받는 중이면 [지원 담당자와 대화] — 이 PC 사람이 **먼저** 말을 걸 수 있게(2026-09-29 사용자 요청).
  const host = useHostSession();

  // 무인 서비스에 붙은 모드에서는 **코드가 없어도 지우지 않는다** — "내 ID 를 알려 주세요" 라는 안내 아래가 비면 고장으로 보인다.
  // 서비스가 멈춰 있다는 사실 자체를 말한다(앱이 대신 에이전트가 되지는 않는다 — 기기 코드가 둘로 갈라지는 원래 문제가 돌아온다).
  if (!deviceCode) {
    if (!SHELL.unattendedHost) return null;
    return (
      <section className="home-section">
        <h2 className="home-h">내 기기 정보</h2>
        <div className="hint">
          무인 원격지원 서비스가 <b>실행 중이 아닙니다.</b> 서비스가 시작되면 여기에 내 ID 와 세션 비밀번호가 표시됩니다.
          <br />
          계속 이 상태라면 관리자에게 문의하세요(관리자 권한으로 <code>sc start RemoteSupportAgent</code>).
        </div>
      </section>
    );
  }

  return (
    <section className="home-section">
      <h2 className="home-h">내 기기 정보</h2>
      {/* 원격 접속 중 — 대화 창 입구. 접속이 없을 때는 대화 통로가 없어 보이지 않는다. */}
      {host.active && (
        <div className="host-session-bar">
          <span>
            <b>원격 접속 중</b> — 지원 담당자가 이 PC 를 보고 있습니다.
          </span>
          <button onClick={host.openChat}>💬 지원 담당자와 대화</button>
        </div>
      )}
      <div className="device-ids">
        <div style={offline ? { opacity: 0.45 } : undefined}>
          <div className="id-label">내 ID</div>
          <div className="mono-big">{deviceCode}</div>
        </div>
        {sessionPw && (
          <div>
            <div className="id-label">
              세션 비밀번호
              {IS_SHELL && (
                <button className="chip-btn" onClick={regenerate} disabled={regenerating} title="세션 비밀번호를 새로 만듭니다">
                  {regenerating ? '재생성 중…' : '재생성'}
                </button>
              )}
            </div>
            <div className="mono-big">{sessionPw}</div>
          </div>
        )}
      </div>

      {/* 🔴 결함 ⑨ — 코드와 비밀번호가 떠 있으면 사용자는 "접속 받을 수 있다" 고 믿고 불러 준다.
          서버가 받아 주지 않는 동안은 그게 사실이 아니므로 **먼저** 말한다. */}
      {offline && (
        <div className="conn-warn">
          ⚠ <b>서버에 연결되어 있지 않습니다.</b> 지금은 이 ID 로 접속을 받을 수 없습니다. 네트워크를 확인해 주세요.
        </div>
      )}

      {/* 🔴 macOS 권한 — 없으면 세션은 "연결됨" 인데 상대는 검은 화면을 보고 조작도 못 한다. */}
      {missing.length > 0 && (
        <div className="conn-warn">
          ⚠ <b>이 Mac 은 아직 원격지원을 제대로 받을 수 없습니다.</b>
          <ul style={{ margin: '6px 0' }}>
            {missing.map((m) => (
              <li key={m.kind}>
                <b>{m.label}</b> 권한 없음 — {m.effect} <button onClick={() => openSettings(m.kind)}>설정 열기</button>
              </li>
            ))}
          </ul>
          시스템 설정 &gt; 개인정보 보호 및 보안에서 <b>RemoteSupport</b> 를 켜 주세요. 켠 뒤에도 이 경고가 남아 있으면 앱을
          다시 시작하세요. <button onClick={restartApp}>앱 다시 시작</button>
        </div>
      )}

      {/* ⚠️ 피제어 승인 대화상자가 없다 — **비밀번호를 불러 주는 행위가 곧 동의**다(§17.9.3). */}
      {sessionPw && (
        <div className="hint" style={{ marginTop: 8 }}>
          이 값을 알려 주면 상대가 <b>내 화면을 보고 마우스·키보드를 조작</b>할 수 있습니다. 아는 사람에게만 알려 주세요.
        </div>
      )}
    </section>
  );
}
